import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import type { Components } from 'react-markdown';

interface MarkdownRendererProps {
  content: string;
  className?: string;
}

const components: Components = {
  h1: ({ children, ...props }) => (
    <h1 className="text-3xl md:text-4xl font-bold mt-10 mb-4 font-serif" {...props}>{children}</h1>
  ),
  h2: ({ children, ...props }) => (
    <h2 className="text-2xl md:text-3xl font-bold mt-8 mb-3 font-serif" {...props}>{children}</h2>
  ),
  h3: ({ children, ...props }) => (
    <h3 className="text-xl md:text-2xl font-semibold mt-6 mb-2 font-serif" {...props}>{children}</h3>
  ),
  h4: ({ children, ...props }) => (
    <h4 className="text-lg md:text-xl font-semibold mt-5 mb-2" {...props}>{children}</h4>
  ),
  p: ({ children, ...props }) => (
    <p className="text-base md:text-lg leading-relaxed mb-5 text-foreground/90" {...props}>{children}</p>
  ),
  a: ({ children, href, ...props }) => (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className="text-primary underline underline-offset-2 decoration-primary/40 hover:decoration-primary transition-colors"
      {...props}
    >
      {children}
    </a>
  ),
  img: ({ src, alt, ...props }) => (
    <figure className="my-8">
      <img
        src={src}
        alt={alt ?? ''}
        className="w-full rounded-lg shadow-sm"
        loading="lazy"
        {...props}
      />
      {alt && (
        <figcaption className="text-sm text-muted-foreground mt-2 text-center italic">
          {alt}
        </figcaption>
      )}
    </figure>
  ),
  blockquote: ({ children, ...props }) => (
    <blockquote
      className="border-l-4 border-primary/30 pl-5 my-6 italic text-muted-foreground"
      {...props}
    >
      {children}
    </blockquote>
  ),
  ul: ({ children, ...props }) => (
    <ul className="list-disc list-outside pl-6 mb-5 space-y-1" {...props}>{children}</ul>
  ),
  ol: ({ children, ...props }) => (
    <ol className="list-decimal list-outside pl-6 mb-5 space-y-1" {...props}>{children}</ol>
  ),
  li: ({ children, ...props }) => (
    <li className="text-base md:text-lg leading-relaxed text-foreground/90" {...props}>{children}</li>
  ),
  code: ({ children, className, ...props }) => {
    const isBlock = className?.includes('language-');
    if (isBlock) {
      return (
        <code className={`${className ?? ''} block bg-muted/50 rounded-lg p-4 my-4 overflow-x-auto text-sm font-mono`} {...props}>
          {children}
        </code>
      );
    }
    return (
      <code className="bg-muted/50 px-1.5 py-0.5 rounded text-sm font-mono" {...props}>
        {children}
      </code>
    );
  },
  pre: ({ children, ...props }) => (
    <pre className="bg-muted/50 rounded-lg p-4 my-6 overflow-x-auto" {...props}>{children}</pre>
  ),
  hr: (props) => <hr className="my-8 border-border" {...props} />,
  em: ({ children, ...props }) => (
    <em className="italic" {...props}>{children}</em>
  ),
  strong: ({ children, ...props }) => (
    <strong className="font-bold" {...props}>{children}</strong>
  ),
  table: ({ children, ...props }) => (
    <div className="overflow-x-auto my-6">
      <table className="min-w-full border border-border rounded-lg" {...props}>{children}</table>
    </div>
  ),
  th: ({ children, ...props }) => (
    <th className="border border-border px-4 py-2 bg-muted/50 text-left font-semibold" {...props}>{children}</th>
  ),
  td: ({ children, ...props }) => (
    <td className="border border-border px-4 py-2" {...props}>{children}</td>
  ),
};

export function MarkdownRenderer({ content, className }: MarkdownRendererProps) {
  return (
    <div className={className}>
      <ReactMarkdown remarkPlugins={[remarkGfm]} components={components}>
        {content}
      </ReactMarkdown>
    </div>
  );
}
